// ─── FACTOR EXPOSURE PANEL (Phase 2) ──────────────────────────────────────────
// Self-contained: fetches its own price history, computes factor scores from the
// factor engine, and renders portfolio factor exposures + independent-position
// analysis. Pushes portfolioExposures onto globalSignals via onData so the
// Signal Board's PORTFOLIO IMPLICATION section (Phase 1B) activates.

import { useState, useEffect } from "react";
import { C, s, fmt, backendFetch, ALPACA_REST } from "./core";
import {
  computeFactorScores,
  computePortfolioExposures,
  countIndependentPositions,
  computeValueFactor,
  computeQualityFactor,
} from "./factorEngine";
import { getFactors } from "./frenchFactors";

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

// Weighted average of a factor across holdings (re-normalized over non-null
// values) — used for the portfolio-level value/quality exposures, which
// computePortfolioExposures intentionally leaves null.
function weightedFactor(holdings, factorScores, key) {
  let acc = 0;
  let wsum = 0;
  for (const h of holdings) {
    const v = factorScores[h.symbol]?.[key];
    if (isNum(v)) {
      acc += h.weight * v;
      wsum += h.weight;
    }
  }
  return wsum > 0 ? acc / wsum : null;
}

// Factor rows in display order. `key` matches computePortfolioExposures output.
const FACTOR_ROWS = [
  { key: "beta",       label: "Market Beta" },
  { key: "momentum",   label: "Momentum" },
  { key: "volatility", label: "Volatility" },
  { key: "size",       label: "Size" },
  { key: "value",      label: "Value" },
  { key: "quality",    label: "Quality" },
];

function interpret(key, v) {
  if (v == null) {
    return key === "value" || key === "quality" ? "—  Add fundamental data" : "—";
  }
  switch (key) {
    case "beta":
      if (v < 0) return "Inverse";
      if (v > 1.3) return "High market sensitivity";
      if (v >= 0.7) return "Market-like";
      return "Defensive";
    case "momentum":
      if (v > 0.15) return "Recent winner tilt";
      if (v < -0.15) return "Recent loser tilt";
      return "Neutral momentum";
    case "volatility":
      if (v > 0.3) return "Elevated volatility";
      if (v >= 0.15) return "Moderate volatility";
      return "Low volatility";
    case "size":
      if (v > 0.7) return "Large cap tilt";
      if (v >= 0.3) return "Mid cap tilt";
      return "Small cap tilt";
    case "value":
      if (v > 0.7) return "Deep value — cheap on P/B and P/E";
      if (v >= 0.4) return "Moderate value tilt";
      if (v >= 0.3) return "Neutral valuation";
      return "Growth tilt — premium valuation";
    case "quality":
      if (v > 0.7) return "High quality — strong ROE, low leverage";
      if (v >= 0.4) return "Moderate quality";
      if (v >= 0.3) return "Average quality";
      return "Low quality — weak ROE or high leverage";
    default:
      return "";
  }
}

// Horizontal magnitude bar: |value| clamped to [0,2] mapped to the 160px track,
// green for positive exposure, red for negative.
function FactorBar({ v }) {
  const clamped = v == null ? 0 : Math.max(-2, Math.min(2, v));
  const widthPct = (Math.abs(clamped) / 2) * 100;
  const fill = v == null ? "transparent" : v >= 0 ? C.green : C.red;
  return (
    // ⚠⚠ "0 1 160px", NOT width:160 + flexShrink:0, AND NOT flex:1.
    // The old pair made this the widest immovable child in its row: 120 label +
    // 160 bar + 48 value + 30 gaps = 358px hard floor in a 366px panel on a
    // folded phone, so the interpretation text — the only shrinkable child —
    // absorbed all of it, collapsed to one word per line, and the row overflowed
    // anyway into an ancestor that clips without scrolling.
    // ⚠ grow 0 is deliberate: flex:1 sets basis 0 and GROWS, which would stretch
    // this meter across the whole panel on a desktop and wreck the rhythm of six
    // aligned rows. basis 160 keeps the desktop rendering byte-identical to what
    // shipped; only the shrink path is new.
    // ⚠ THE WIDTH WAS NEVER THE POINT — COMPARABILITY ACROSS ROWS IS. The fill is
    // a percentage of the track, so six tracks of any equal width read the same.
    // They stay equal because every row is the same flex line in the same
    // container. minWidth 56 is where a proportion stops being legible; it is a
    // judgement, not a measurement, and is the one number here worth adjusting.
    <div style={{ flex: "0 1 160px", minWidth: 56, height: 8, background: C.border,
                  borderRadius: 2, overflow: "hidden" }}>
      <div style={{ width: `${widthPct}%`, height: "100%", background: fill }} />
    </div>
  );
}

async function fetchCloses(symbol) {
  try {
    const res = await backendFetch(
      `${ALPACA_REST}/stocks/${symbol}/bars?timeframe=1Day&limit=252&feed=iex`,
      `/api/bars/${symbol}?tf=1D&limit=252`
    );
    if (!res.ok) return null;
    const data = await res.json();
    const closes = (data.bars || [])
      .map((b) => b.c)
      .filter((c) => typeof c === "number" && Number.isFinite(c));
    return closes.length ? closes : null;
  } catch {
    return null;
  }
}

export function FactorExposurePanel({ symbols, globalSignals, onData }) {
  // globalSignals is accepted for interface completeness (reserved for future
  // cross-signal context); Phase 2 display does not read it.
  void globalSignals;

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [result, setResult] = useState(null); // { factorScores, exposures, independent, loaded, total }

  const symbolsKey = (symbols || []).join(",");

  // Single sequential flow: price-derived factors render immediately, then EDGAR
  // value/quality is layered on. One async function, top to bottom — no state
  // dependency between the price step and the EDGAR step.
  useEffect(() => {
    if (!symbols || symbols.length < 2) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    (async () => {
      let factorScores;
      let loadedSymbols;
      let holdings;
      let lastClose;
      let independent;
      let uniq;

      // ── Steps 1-3: price history → price-derived factors → display ──
      try {
        uniq = [...new Set(symbols)];

        // Step 1: price history for every symbol + SPY benchmark, in parallel.
        const [spyCloses, ...symClosesList] = await Promise.all([
          fetchCloses("SPY"),
          ...uniq.map((sym) => fetchCloses(sym)),
        ]);
        await getFactors().catch(() => []); // warm French cache (later phases)
        if (cancelled) return;

        if (!spyCloses || spyCloses.length < 2) {
          setFailed(true);
          setLoading(false);
          return;
        }

        // Step 2: price-derived factor scores per loaded symbol.
        factorScores = {};
        loadedSymbols = [];
        lastClose = {}; // most recent close per symbol → price for EDGAR
        uniq.forEach((sym, i) => {
          const closes = symClosesList[i];
          if (!closes) return; // skip failed symbols silently
          factorScores[sym] = computeFactorScores(sym, closes, spyCloses, null, null);
          lastClose[sym] = closes[closes.length - 1];
          loadedSymbols.push(sym);
        });

        if (loadedSymbols.length < 2) {
          setResult({ factorScores, exposures: null, independent: null, loaded: loadedSymbols.length, total: uniq.length });
          setLoading(false);
          return;
        }

        const weight = 1 / loadedSymbols.length;
        holdings = loadedSymbols.map((sym) => ({ symbol: sym, weight }));
        const exposures = computePortfolioExposures(holdings, factorScores);
        independent = countIndependentPositions(holdings, factorScores);

        // Step 3: push the price-derived result immediately (beta/momentum/etc).
        if (cancelled) return;
        setResult({ factorScores, exposures, independent, loaded: loadedSymbols.length, total: uniq.length });
        setLoading(false);
        if (onData) onData("portfolioExposures", exposures);
        // Additive: publish the portfolio independent-positions result
        // ({ totalHoldings, independentPositions, groups }) for consumers like
        // the Convergence Board's Portfolio Context card.
        if (onData) onData("portfolioIndependentPositions", independent);
      } catch {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
        return;
      }

      // ── Steps 4-6: EDGAR (additive) — own try so a failure keeps price-derived ──
      try {
        // Step 4: EDGAR availability, direct authenticated fetch.
        const backendUrl = window.QUANT_SIGNAL_BACKEND || import.meta.env.VITE_BACKEND_URL;
        if (!backendUrl) return;

        const availRes = await fetch(backendUrl + "/api/factors/edgar/AAPL?price=213", {
          headers: {
            "Authorization": "Bearer " + localStorage.getItem("qs_auth_token"),
            "Content-Type": "application/json",
          },
        });
        if (!availRes.ok) return;
        const availData = await availRes.json();
        if (cancelled) return;
        if (!availData.available) return; // no fundamentals → keep price-derived display

        // Step 5: bulk EDGAR fetch (prices = last close per loaded symbol).
        const prices = {};
        for (const sym of loadedSymbols) {
          if (isNum(lastClose[sym])) prices[sym] = lastClose[sym];
        }
        const bulkRes = await fetch(backendUrl + "/api/factors/edgar/bulk", {
          method: "POST",
          headers: {
            "Authorization": "Bearer " + localStorage.getItem("qs_auth_token"),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ symbols: loadedSymbols, prices }),
        });
        const edgar = await bulkRes.json();
        if (cancelled) return;

        // Step 6: value/quality factors, recompute exposures, push, update display.
        const avail = loadedSymbols.filter((sym) => edgar[sym]?.available === true);
        if (avail.length === 0) return;
        const universe_pb = avail.map((s2) => edgar[s2].pb).filter(isNum);
        const universe_pe = avail.map((s2) => edgar[s2].pe).filter(isNum);
        const universe_roe = avail.map((s2) => edgar[s2].roe).filter(isNum).map((r) => Math.min(r, 5.0));
        const universe_de = avail.map((s2) => edgar[s2].de).filter(isNum);

        for (const sym of loadedSymbols) {
          const d = edgar[sym];
          if (d?.available === true) {
            factorScores[sym].value = computeValueFactor(d.pb, d.pe, universe_pb, universe_pe);
            factorScores[sym].quality = computeQualityFactor(d.roe, d.de, universe_roe, universe_de);
            factorScores[sym].dataSource = "sec_edgar";
            factorScores[sym].filedDate = d.filed_date;
          }
        }

        // computePortfolioExposures leaves value/quality null — layer them in.
        const newExposures = computePortfolioExposures(holdings, factorScores);
        newExposures.value = weightedFactor(holdings, factorScores, "value");
        newExposures.quality = weightedFactor(holdings, factorScores, "quality");

        if (cancelled) return;
        setResult({ factorScores: { ...factorScores }, exposures: newExposures, independent, loaded: loadedSymbols.length, total: uniq.length });
        if (onData) onData("portfolioExposures", newExposures);
      } catch {
        // EDGAR failed — price-derived factors are already showing; no error to user.
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey, onData]);

  // Only renders with at least 2 symbols in the watchlist.
  if (!symbols || symbols.length < 2) return null;

  // SEC EDGAR presence, derived from the (possibly EDGAR-updated) factor scores.
  const scores = result?.factorScores || {};
  const edgarScores = Object.values(scores).filter((sc) => sc?.dataSource === "sec_edgar");
  const edgarCount = edgarScores.length;
  const edgarActive = edgarCount > 0;
  const edgarFiledDate = edgarScores.map((sc) => sc.filedDate).filter(Boolean).sort().pop() || null;

  return (
    <div style={s.panel}>
      <div style={s.panelB}>
        {/* Header */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, letterSpacing: "0.15em" }}>
          PORTFOLIO FACTOR EXPOSURE
        </div>

        {loading && (
          <div style={{ fontSize: 10, color: C.textDim, marginTop: 8 }}>
            Computing factor exposures…
          </div>
        )}

        {!loading && failed && (
          <div style={{ fontSize: 10, color: C.textDim, marginTop: 8 }}>
            Factor analysis unavailable — could not load price data.
          </div>
        )}

        {!loading && !failed && result && result.loaded < 2 && (
          <div style={{ fontSize: 10, color: C.textDim, marginTop: 8 }}>
            Add at least 2 symbols to your watchlist to see factor analysis
          </div>
        )}

        {!loading && !failed && result && result.loaded >= 2 && (
          <>
            {/* Subheader */}
            <div style={{ fontSize: 9, color: C.textDim, marginTop: 3, marginBottom: 12 }}>
              {result.loaded < result.total
                ? `${result.loaded} of ${result.total} symbols loaded`
                : `${result.loaded} holdings`}{" "}
              · equal-weighted · {edgarActive ? "fundamental data" : "price-derived"}
            </div>

            {/* Factor exposure bars */}
            {FACTOR_ROWS.map(({ key, label }) => {
              const v = result.exposures?.[key] ?? null;
              const showSource =
                edgarActive && v != null && (key === "value" || key === "quality");
              return (
                <div key={key} style={{ marginBottom: 7 }}>
                  {/* ⚠ flexWrap is the BACKSTOP, not the fix. The bar shrinking
                      (see FactorBar) is what keeps this on one line at ordinary
                      phone widths; this catches the case where even a 56px meter
                      plus the label and value leave the interpretation nothing,
                      and drops that sentence to its own line instead of pushing
                      the row into an ancestor that clips without scrolling.
                      ⚠ WRAP, NOT overflowX — same call ThesisDesk documents for
                      its ticker cards: a horizontal scroller hides content behind
                      a bar that is easy to miss, and a factor reading is a short
                      row with a trailing sentence, not a wide table.
                      ⚠ rowGap 4, columnGap 10: `gap: 10` applied to both axes, so
                      a wrapped line used to sit as far below its row as the
                      columns sit apart, which read as a new row rather than a
                      continuation. */}
                  <div style={{ display: "flex", alignItems: "center",
                                flexWrap: "wrap", columnGap: 10, rowGap: 4 }}>
                    <div style={{ fontSize: 10, color: C.textDim, width: 120, flexShrink: 0 }}>{label}</div>
                    <FactorBar v={v} />
                    <div style={{ fontSize: 10, color: C.text, width: 48, flexShrink: 0, textAlign: "right" }}>
                      {v == null ? "—" : v.toFixed(2)}
                    </div>
                    <div style={{ fontSize: 9, color: C.textDim }}>{interpret(key, v)}</div>
                  </div>
                  {showSource && (
                    <div style={{ fontSize: 9, color: C.textDim, marginLeft: 130, marginTop: 1 }}>
                      Fundamental data{edgarFiledDate ? ` · Filed ${edgarFiledDate}` : ""}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Independent positions analysis */}
            <IndependentPositions independent={result.independent} />

            {/* Disclaimer */}
            <div style={{ fontSize: 10, fontWeight: 700, color: "#FFD700", marginTop: 14, lineHeight: 1.6 }}>
              {edgarActive
                ? "Factor exposures computed from price history and public company filings (as-filed basis). Value and quality factors use most recently filed 10-Q/10-K data. Fundamental ratios are as-reported and may differ from consensus estimates. For informational purposes only. Not a recommendation — consult a licensed financial advisor for investment decisions."
                : "Factor exposures computed from price history using equal weighting. For informational purposes only. Not a recommendation — consult a licensed financial advisor for investment decisions."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ⚠ EXPORTED FOR THE CONVERGENCE BOARD. It renders in two places now: here, as a
// sub-section under the factor bars, and as the whole body of the board's YOUR
// WATCHLIST slot. It stays a pure presentational function of one object — no
// state, no effects, no fetch — which is the only reason a second mount is free.
//
// ⚠⚠ `flush` EXISTS BECAUSE THE SEPARATOR IS A PROPERTY OF THE SLOT, NOT OF THIS
// COMPONENT. Here it follows the factor bars and needs its own rule to divide
// them; on the board it IS the section body and the parent has already drawn a
// divider above the YOUR WATCHLIST header, so the same rule lands as a second
// line under a heading with nothing between them. It defaults FALSE, so this
// file's own render is byte-identical to before.
export function IndependentPositions({ independent, flush = false }) {
  if (!independent) return null;
  const { totalHoldings: N, independentPositions: M, groups } = independent;
  const sqrtM = Math.sqrt(M);
  const sqrtM1 = Math.sqrt(M + 1);
  const improvement = M > 0 ? (sqrtM1 / sqrtM - 1) * 100 : 0;

  return (
    <div style={flush
      ? {}
      : { marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.text, letterSpacing: "0.12em", marginBottom: 8 }}>
        INDEPENDENT POSITIONS ANALYSIS
      </div>

      {/* Summary */}
      <div style={{ fontSize: 10, color: C.text, lineHeight: 1.6 }}>You have {N} holdings.</div>
      <div style={{ fontSize: 10, color: C.text, lineHeight: 1.6 }}>
        You have {M} independent position{M === 1 ? "" : "s"}.
      </div>
      {M < N && (
        <div style={{ fontSize: 10, color: C.amber, lineHeight: 1.6, marginTop: 2 }}>
          Your {N - M} correlated holding{N - M === 1 ? "" : "s"} reduce your effective diversification.
        </div>
      )}

      {/* Position groups */}
      <div style={{ marginTop: 10 }}>
        {groups.map((g, i) => {
          const wpct = (g.combinedWeight || 0) * 100;
          return (
            <div key={`${g.dominantFactor}-${i}`} style={{ ...s.card(C.border), marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: C.amber, fontWeight: 700 }}>{g.name}</div>
              <div style={{ fontSize: 10, color: C.text, marginTop: 2 }}>{g.holdings.join(", ")}</div>
              <div style={{ fontSize: 9, color: C.textDim, marginTop: 2 }}>
                Combined weight: {wpct.toFixed(0)}%
              </div>
              {g.combinedWeight > 0.5 && (
                <div style={{ fontSize: 9, color: C.amber, marginTop: 2 }}>
                  This is ONE independent position expressed {g.holdings.length} ways.
                </div>
              )}
              {g.dominantFactor === "momentum" && g.combinedWeight > 0.4 && (
                <div style={{ fontSize: 9, color: C.textDim, marginTop: 2 }}>
                  Momentum factor historically mean-reverts after 12-18 months of outperformance.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Information Ratio preview */}
      <div style={{ fontSize: 10, color: C.textDim, marginTop: 8 }}>
        IR ∝ skill × √{M} = skill × {fmt(sqrtM, 2)}
      </div>
      <div style={{ fontSize: 9, color: C.textDim, marginTop: 2 }}>
        Adding one uncorrelated position: skill × {fmt(sqrtM1, 2)} (+{improvement.toFixed(0)}% edge)
      </div>
    </div>
  );
}
